import React, { useState, useRef } from "react";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Container,
  Row,
  Col,
  NavbarBrand,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { REGEX } from "variables/constants";
import { forgetPassword } from "actions/forgetPassword";
import img from "assets/img/icons/dashboard2.jpg";
import { BrandLogo } from "components/BrandLogo/BrandLogo";

const initialFields = {
  email: { value: "", error: "" },
};

export const ForgetPassword = () => {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const [forgotPasswordInfo, setForgotPasswordInfo] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [focusedEmail, setFocusedEmail] = useState(false);

  //==> notify function is used for to show notification.
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //==> handleChange function is used for set input values in a state.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordInfo((prev) => ({
      ...prev,
      [name]: { value: value.trim(), error: "" },
    }));
  };

  //==> getValidationError used for to check email validation.
  const getValidationError = (value) => {
    if (!value) return "Please enter an email";
    if (!REGEX.EMAIL_REGEX.test(value)) return "Please enter a valid email";
    return "";
  };

  const handleOnBlur = (value) => {
    setFocusedEmail(false);
    const error = getValidationError(value.trim());
    setForgotPasswordInfo((prev) => ({
      ...prev,
      email: { value: value.trim(), error },
    }));
  };

  const validateForm = () => {
    const error = getValidationError(forgotPasswordInfo.email.value);
    setForgotPasswordInfo((prev) => ({
      ...prev,
      email: { ...prev.email, error },
    }));
    return !error;
  };

  //==> handleSubmit function is used for submit the api call.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setShowLoaderOnClick(true);
      const res = await forgetPassword({
        email: forgotPasswordInfo.email.value,
      });

      if (res?.code === 200) {
        notify("success", res.message || "Recovery link sent");
      } else {
        notify("danger", res?.message || "Something went wrong");
      }
    } catch {
      notify("danger", "Failed to send recovery link");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <div className="login-page">
        {/* BACKGROUND IMAGE */}
        <div
          className="login-bg-image"
          style={{ backgroundImage: `url(${img})` }}
        />

        {/* GRADIENT OVERLAY */}
        <div className="login-bg-overlay" />

        {/* CONTENT */}
        <Container fluid className="login-content">
          <Row className="min-vh-100 align-items-center">
            {/* LEFT : BRAND */}
            <Col
              lg="6"
              className="d-none d-lg-flex align-items-center justify-content-center"
            >
              <NavbarBrand className="p-0">
                {/* <div className="display-1 font-weight-bold text-info text-center">
                  TrueLift Analytics
                </div> */}
                <BrandLogo />
              </NavbarBrand>
            </Col>

            {/* RIGHT : FORGOT PASSWORD CARD */}
            <Col
              lg="6"
              md="6"
              className="d-flex justify-content-center align-items-center"
            >
              <div className="login-card-wrapper">
                <Card className="border-0 shadow-lg rounded-lg py-5">
                  <CardBody className="login-card-body">
                    <div className="text-center mb-4">
                      <h2 className="font-weight-bold mb-1">Forgot Password</h2>
                      <p className="text-muted">
                        Enter your email to receive a recovery link
                      </p>
                    </div>

                    <Form onSubmit={handleSubmit}>
                      <FormGroup
                        className={classnames("mb-3", {
                          focused: focusedEmail,
                        })}
                      >
                        <InputGroup
                          className={classnames(
                            "input-group-merge",
                            forgotPasswordInfo.email.error && "has-danger",
                          )}
                        >
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="ni ni-email-83" />
                            </InputGroupText>
                          </InputGroupAddon>

                          <Input
                            type="email"
                            name="email"
                            bsSize="lg"
                            placeholder="Email"
                            value={forgotPasswordInfo.email.value}
                            onChange={handleChange}
                            onBlur={(e) => handleOnBlur(e.target.value)}
                            onFocus={() => setFocusedEmail(true)}
                            autoComplete="off"
                          />
                        </InputGroup>

                        {forgotPasswordInfo.email.error && (
                          <small className="text-warning">
                            {forgotPasswordInfo.email.error}
                          </small>
                        )}
                      </FormGroup>

                      <Button
                        block
                        // color="primary"
                        style={{ backgroundColor: "#00acb4", color: "#f6f7f8" }}
                        size="lg"
                        type="submit"
                        disabled={showLoaderOnClick}
                      >
                        {showLoaderOnClick
                          ? "Sending..."
                          : "Send Recovery Email"}
                      </Button>

                      <div className="text-center mt-4 d-flex align-items-center justify-content-center">
                        <span
                          className="d-flex align-items-center"
                          style={{ color: "#00acb4", cursor: "pointer" }}
                          onClick={() => navigate("/auth/login")}
                        >
                          <i className="fa-solid fa-chevron-left mr-2"></i>
                          Back to Login
                        </span>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};
